import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import MainLayout from '../layouts/MainLayout.vue'
import { authState, hasPermission, getPrimaryRoleName } from '../config/auth'
import { isConfiguredMenuPathAllowed } from '../config/menu'
import { toast } from '../utils/toast'

type RoutePermission = string | string[]

const appBase = import.meta.env.BASE_URL || '/'

const normalizeBasePath = (base: string) => {
  if (!base || base === '/') return ''
  const withLeadingSlash = base.startsWith('/') ? base : `/${base}`
  return withLeadingSlash.replace(/\/$/, '')
}

const normalizeLegacyMobileEntry = () => {
  if (typeof window === 'undefined') return

  const basePath = normalizeBasePath(appBase)
  const currentPath = window.location.pathname.replace(/\/$/, '') || '/'
  const legacyMobilePath = `${basePath}/m`

  if (currentPath !== legacyMobilePath) return

  const rawHashPath = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
  let targetHashPath = rawHashPath && rawHashPath !== '/' ? rawHashPath : '/m'

  if (!targetHashPath.startsWith('/')) {
    targetHashPath = `/${targetHashPath}`
  }

  if (targetHashPath === '/login') {
    targetHashPath = '/m/login'
  } else if (targetHashPath === '/dashboard' || targetHashPath === '/m/dashboard') {
    targetHashPath = '/m'
  } else if (!targetHashPath.startsWith('/m')) {
    targetHashPath = `/m${targetHashPath}`
  }

  if (window.location.search && !targetHashPath.includes('?')) {
    targetHashPath += window.location.search
  }

  const targetUrl = `${basePath || ''}/#${targetHashPath}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`

  if (currentUrl !== targetUrl) {
    window.location.replace(targetUrl)
  }
}

normalizeLegacyMobileEntry()

// 0.2.9: 路由权限兜底表。优先读取 route.meta.permission；
// 未补 meta 的历史页面按 Sidebar 当前菜单权限兜底，避免直输 URL 绕过菜单过滤。
const ROUTE_PERMISSION_FALLBACKS: Record<string, RoutePermission> = {
  ParentProduct: 'product:view',
  ParentProductDetail: 'product:view',
  TechPackView: 'product:view',
  MeasurementTemplateManagement: 'product:view',
  CareInstructionLibrary: 'sys:dict',
  LabelTemplateCenter: ['label-template:view', 'label-print:view', 'label-print:execute', 'sys:dict'],
  ProductCategoryView: 'product:view',
  DesignWorkOrderDetail: ['design:view', 'design:create'],
  PrototypeWorkOrderList: ['prototype:view', 'prototype:create'],
  PrototypeWorkspace: ['prototype:view', 'prototype:create'],
  SampleWorkTicket: ['prototype:view', 'prototype:create'],
  SampleLibrary: 'prototype:view',
  BOMList: 'bom:view',
  DesignBOMDetail: 'bom:view',
  MfgBOMList: 'bom:view',
  AdvancedBOMWorkspace: 'bom:view',

  SalesOrderCenter: ['order:view', 'order:create'],
  SalesOrderDetail: ['order:view', 'order:create'],
  ProductionDashboard: 'prod:view',
  ManufacturingOrderDetail: 'prod:view',
  ProductionPlanGantt: 'prod:view',

  RoutingManagement: 'routing:manage',
  WorkInstructionList: 'prod:view',
  CuttingExecution: 'prod:view',
  ShopFloorExecution: ['report:self', 'report:all'],
  SupervisorRapidReport: 'report:all',
  FinishedGoodsQC: ['qc:inspect', 'qc:manage'],
  TaskBoard: 'prod:view',
  TaskBoardDetail: 'prod:view',
  OutsourceSupplierProfile: 'outsource:manage',

  CustomerManagement: 'customer:manage',
  MaterialMasterDetail: ['material:view', 'material:manage'],
  OrderProcurementCenter: ['po:view', 'po:create'],
  MaterialRequirementPool: ['po:view', 'po:create'],
  AccessorySupplyWorkbench: ['po:view', 'po:create', 'inventory:view', 'inventory:operate', 'warehouse:manage'],
  ReceivingList: ['inventory:operate', 'warehouse:manage'],
  FabricInbound: ['inventory:operate', 'warehouse:manage'],
  FabricInventory: 'inventory:view',
  WarehouseIssueStation: 'warehouse:manage',
  InventoryOperationCenter: 'inventory:operate',
  InventoryTransactionList: 'inventory:view',
  FinishedGoodsInbound: 'inventory:operate',
  FinishedGoodsHub: 'inventory:operate',

  OutsourceFinanceCenter: 'finance:view',
  CustomerReceivableCenter: 'finance:view',
  PayrollManagement: 'finance:view',
  FeeManagement: 'finance:view',
  CostDecisionCenter: 'finance:view',
  MenuManagement: 'system:menu:view',

  GlobalSpecDict: 'sys:dict',
  ColorDictionaryManagement: 'sys:dict',
  SizeGroupManagement: 'sys:dict',
  CategoryMetadataManagement: 'sys:dict',
  CategoryPomBindingManagement: 'sys:dict',
  SpecComboTemplateManagement: 'sys:dict',
  CategoryManagement: 'sys:dict',
  CategoryConfigHub: 'sys:dict',
  POMDictionary: 'sys:dict',
  OperationCategory: 'sys:dict',
  UnitDict: 'sys:dict',
  UnitConversion: 'sys:dict',
  ComingSoonPrintTpl: 'sys:dict',
  ComingSoonStockLog: 'inventory:view',
  ComingSoonSupplierAP: 'finance:view',
}

const resolveRoutePermission = (to: { meta: Record<string, any>; name?: unknown }): RoutePermission | undefined =>
  (to.meta.permission as RoutePermission | undefined) ||
  (to.name ? ROUTE_PERMISSION_FALLBACKS[String(to.name)] : undefined)

const routes: RouteRecordRaw[] = [
  // 登录页 - 默认首页
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: {
      title: '登录',
      public: true // 公开页面，无需登录
    }
  },
  // 主布局 - 所有业务路由都在此布局下
  {
    path: '/',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: 'dashboard'
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/Dashboard.vue'),
        meta: {
          title: '工作台',
          breadcrumb: ['首页', '工作台'],
          requiresAuth: true
        }
      },
      {
        path: '403',
        name: 'Forbidden',
        component: () => import('../views/Forbidden.vue'),
        meta: {
          title: '访问受限',
          breadcrumb: ['首页', '访问受限'],
          requiresAuth: true
        }
      },
      {
        path: 'rd-plan-workspace',
        name: 'RDPlanWorkspace',
        component: () => import('../views/RDPlanWorkspace.vue'),
        meta: {
          title: '设计研发计划',
          breadcrumb: ['首页', '产品与设计管理', '设计研发计划'],
          requiresAuth: true,
          permission: ['design:view', 'design:create']
        }
      },
      {
        path: 'design-work-orders',
        name: 'DesignWorkOrderList',
        component: () => import('../views/DesignWorkOrderList.vue'),
        meta: {
          title: '设计工单管理',
          breadcrumb: ['首页', '产品与设计管理', '设计工单管理'],
          requiresAuth: true,
          permission: ['design:view', 'design:create']
        }
      },
      {
        path: 'design-work-orders/:id',
        name: 'DesignWorkOrderDetail',
        component: () => import('../views/DesignWorkOrderDetail.vue'),
        meta: {
          title: '设计评审中心',
          breadcrumb: ['首页', '产品与设计管理', '设计工单管理', '评审中心'],
          requiresAuth: true
        }
      },
      {
        path: 'customer-management',
        name: 'CustomerManagement',
        component: () => import('../views/CustomerManagement.vue'),
        meta: {
          title: '客户档案管理',
          breadcrumb: ['首页', '基础配置中心', '客户档案管理'],
          requiresAuth: true
        }
      },

      {
        path: 'parent-product/:id',
        name: 'ParentProductDetail',
        component: () => import('../views/ParentProductDetail.vue'),
        meta: {
          title: '产品详情',
          breadcrumb: ['首页', '产品与设计管理', '产品管理', '商品详情'],
          requiresAuth: true
        }
      },
      {
        path: 'product-category',
        name: 'ProductCategoryView',
        component: () => import('../views/ProductCategoryView.vue'),
        meta: {
          title: '品类规格管理',
          breadcrumb: ['首页', '产品与设计管理', '品类规格管理'],
          requiresAuth: true
        }
      },
      {
        path: 'sample-work-ticket',
        name: 'SampleWorkTicket',
        component: () => import('../views/SampleWorkTicket.vue'),
        meta: {
          title: '样衣工票',
          breadcrumb: ['首页', '产品与设计管理', '样衣工票'],
          requiresAuth: true
        }
      },
      {
        path: 'mfg-bom-list',
        name: 'MfgBOMList',
        component: () => import('../views/MfgBOMList.vue'),
        meta: {
          title: '制造 BOM 列表',
          breadcrumb: ['首页', '工程与生产管理', '制造 BOM 列表'],
          requiresAuth: true
        }
      },
      {
        path: 'bom-management',
        redirect: to => ({
          path: '/advanced-bom-workspace',
          query: to.query
        })
      },
      {
        path: 'advanced-bom-workspace',
        name: 'AdvancedBOMWorkspace',
        component: () => import('../views/AdvancedBOMWorkspace.vue'),
        meta: {
          title: '高级 BOM 工作台',
          breadcrumb: ['首页', '工程与生产管理', '高级 BOM 工作台'],
          requiresAuth: true
        }
      },
      // ===== 订单与排产中心 =====
      {
        path: 'sales-orders',
        name: 'SalesOrderCenter',
        component: () => import('../views/SalesOrderCenter.vue'),
        meta: {
          title: '销售订单管理',
          breadcrumb: ['首页', '订单与排产中心', '销售订单管理'],
          requiresAuth: true
        }
      },
      {
        path: 'sales-orders/:id',
        name: 'SalesOrderDetail',
        component: () => import('../views/SalesOrderDetail.vue'),
        meta: {
          title: '销售订单详情',
          breadcrumb: ['首页', '订单与排产中心', '销售订单管理', '订单详情'],
          requiresAuth: true
        }
      },
      {
        path: 'production-dispatch',
        name: 'ProductionDispatch',
        component: () => import('../views/ProductionDispatch.vue'),
        meta: {
          title: '生产调度台',
          breadcrumb: ['首页', '订单与排产中心', '生产调度台'],
          requiresAuth: true,
          permission: 'prod:dispatch'
        }
      },
      {
        path: 'production-task-dispatcher',
        name: 'ProductionTaskDispatcher',
        component: () => import('../views/ProductionTaskDispatcher.vue'),
        meta: {
          title: '指派中心',
          breadcrumb: ['首页', '订单与排产中心', '指派中心'],
          requiresAuth: true,
          permission: 'prod:dispatch'
        }
      },
      {
        path: 'production-dashboard',
        name: 'ProductionDashboard',
        component: () => import('../views/ProductionDashboard.vue'),
        meta: {
          title: '生产进度全景表',
          breadcrumb: ['首页', '工程与生产管理', '生产进度全景表'],
          requiresAuth: true
        }
      },
      {
        path: 'manufacturing-order/:id',
        name: 'ManufacturingOrderDetail',
        component: () => import('../views/ManufacturingOrderDetail.vue'),
        meta: {
          title: '生产订单详情',
          breadcrumb: ['首页', '工程与生产管理', '生产进度全景表', '订单详情'],
          requiresAuth: true
        }
      },
      {
        path: 'material-master',
        name: 'MaterialMaster',
        component: () => import('../views/MaterialMaster.vue'),
        meta: {
          title: '物料档案管理',
          breadcrumb: ['首页', '基础配置中心', '物料档案管理'],
          requiresAuth: true,
          permission: ['material:view', 'material:manage']
        }
      },
      {
        path: 'material-detail/:id?',
        name: 'MaterialMasterDetail',
        component: () => import('../views/MaterialMasterDetail.vue'),
        meta: {
          title: '物料档案详情',
          breadcrumb: ['首页', '基础配置中心', '物料与供应链', '物料档案详情'],
          requiresAuth: true
        }
      },
      {
        path: 'global-spec-dict',
        name: 'GlobalSpecDict',
        component: () => import('../views/GlobalSpecDict.vue'),
        meta: {
          title: '规格属性字典',
          breadcrumb: ['首页', '基础配置中心', '规格属性字典'],
          requiresAuth: true
        }
      },
      {
        path: 'color-dictionary-management',
        name: 'ColorDictionaryManagement',
        component: () => import('../views/ColorDictionaryManagement.vue'),
        meta: {
          title: '全系统颜色池',
          breadcrumb: ['首页', '基础配置中心', '全系统颜色池'],
          requiresAuth: true
        }
      },
      {
        path: 'size-group-management',
        name: 'SizeGroupManagement',
        component: () => import('../views/SizeGroupManagement.vue'),
        meta: {
          title: '标准尺码组管理',
          breadcrumb: ['首页', '基础配置中心', '标准尺码组管理'],
          requiresAuth: true
        }
      },
      {
        path: 'category-metadata-management',
        name: 'CategoryMetadataManagement',
        component: () => import('../views/CategoryMetadataManagement.vue'),
        meta: {
          title: '分类元数据管理',
          breadcrumb: ['首页', '基础配置中心', '分类元数据管理'],
          requiresAuth: true
        }
      },
      {
        path: 'category-pom-binding-management',
        name: 'CategoryPomBindingManagement',
        component: () => import('../views/CategoryPomBindingManagement.vue'),
        meta: {
          title: '类目-部位绑定',
          breadcrumb: ['首页', '基础配置中心', '类目-部位绑定'],
          requiresAuth: true
        }
      },
      {
        path: 'measurement-template-management',
        name: 'MeasurementTemplateManagement',
        component: () => import('../views/MeasurementTemplateManagement.vue'),
        meta: {
          title: '版型模板库',
          breadcrumb: ['首页', '产品与设计管理', '版型模板库'],
          requiresAuth: true
        }
      },
      {
        path: 'spec-combo-template-management',
        name: 'SpecComboTemplateManagement',
        component: () => import('../views/SpecComboTemplateManagement.vue'),
        meta: {
          title: '规格组合模板管理',
          breadcrumb: ['首页', '基础配置中心', '规格组合模板管理'],
          requiresAuth: true
        }
      },
      {
        path: 'care-instruction-library',
        name: 'CareInstructionLibrary',
        component: () => import('../views/CareInstructionLibrary.vue'),
        meta: {
          title: '洗护图标与组合库',
          breadcrumb: ['首页', '基础配置中心', '洗护图标与组合库'],
          requiresAuth: true,
          permission: 'sys:dict'
        }
      },
      {
        path: 'category-management',
        name: 'CategoryManagement',
        redirect: '/category-metadata-management',
        meta: {
          title: '分类体系管理',
          breadcrumb: ['首页', '基础配置中心', '分类体系管理'],
          requiresAuth: true
        }
      },
      {
        path: 'category-config-hub',
        name: 'CategoryConfigHub',
        redirect: '/category-metadata-management',
        meta: {
          title: '类目特征配置中心',
          breadcrumb: ['首页', '基础配置中心', '类目特征配置中心'],
          requiresAuth: true
        }
      },
      {
        path: 'pom-dictionary',
        name: 'POMDictionary',
        component: () => import('../views/POMDictionary.vue'),
        meta: {
          title: '部位测量字典',
          breadcrumb: ['首页', '基础配置中心', '部位测量字典'],
          requiresAuth: true
        }
      },
      {
        path: 'unit-dict',
        name: 'UnitDict',
        component: () => import('../views/UnitDict.vue'),
        meta: {
          title: '单位字典',
          breadcrumb: ['首页', '基础配置中心', '单位字典'],
          requiresAuth: true
        }
      },
      {
        path: 'warehouse',
        name: 'Warehouse',
        component: () => import('../views/Warehouse.vue'),
        meta: {
          title: '仓库管理',
          breadcrumb: ['首页', '基础配置中心', '仓库管理'],
          requiresAuth: true,
          permission: 'warehouse:manage'
        }
      },
      {
        path: 'supplier',
        name: 'Supplier',
        component: () => import('../views/Supplier.vue'),
        meta: {
          title: '供应商档案',
          breadcrumb: ['首页', '基础配置中心', '供应商档案'],
          requiresAuth: true,
          permission: 'supplier:manage'
        }
      },
      {
        path: 'parent-product',
        name: 'ParentProduct',
        component: () => import('../views/ParentProduct.vue'),
        meta: {
          title: '产品管理',
          breadcrumb: ['首页', '产品与设计管理', '产品管理'],
          requiresAuth: true
        }
      },
      {
        path: 'bom-list',
        name: 'BOMList',
        component: () => import('../views/BOMList.vue'),
        meta: {
          title: '设计 BOM 管理',
          breadcrumb: ['首页', '产品与设计管理', '设计 BOM 管理'],
          requiresAuth: true
        }
      },
      {
        path: 'design-bom/:id',
        name: 'DesignBOMDetail',
        component: () => import('../views/DesignBOMDetail.vue'),
        meta: {
          title: '设计 BOM 明细',
          breadcrumb: ['首页', '产品与设计管理', '设计 BOM 管理', '设计 BOM 明细'],
          requiresAuth: true
        }
      },
      {
        path: 'tech-pack/:id',
        name: 'TechPackView',
        component: () => import('../views/TechPackView.vue'),
        meta: {
          title: '产品工艺单',
          breadcrumb: ['首页', '产品与设计管理', '产品管理', '产品工艺单'],
          requiresAuth: true
        }
      },
      {
        path: 'operation-category',
        name: 'OperationCategory',
        component: () => import('../views/OperationCategory.vue'),
        meta: {
          title: '工序分类字典',
          breadcrumb: ['首页', '基础配置中心', '工序分类字典'],
          requiresAuth: true
        }
      },
      {
        path: 'standard-operation',
        name: 'StandardOperation',
        component: () => import('../views/StandardOperation.vue'),
        meta: {
          title: '标准工序库',
          breadcrumb: ['首页', '基础配置中心', '标准工序库'],
          requiresAuth: true,
          permission: 'routing:manage'
        }
      },
      {
        path: 'routing-management',
        name: 'RoutingManagement',
        component: () => import('../views/RoutingManagement.vue'),
        meta: {
          title: '工艺路线管理',
          breadcrumb: ['首页', '工程与生产管理', '工艺路线管理'],
          requiresAuth: true
        }
      },
      {
        path: 'workshop-management',
        name: 'WorkshopManagement',
        component: () => import('../views/WorkshopManagement.vue'),
        meta: {
          title: '车间与产线管理',
          breadcrumb: ['首页', '基础配置中心', '车间与产线管理'],
          requiresAuth: true,
          permission: 'prod:view'
        }
      },
      {
        path: 'worker-management',
        name: 'WorkerManagement',
        component: () => import('../views/WorkerManagement.vue'),
        meta: {
          title: '工人档案与薪资定义',
          breadcrumb: ['首页', '基础配置中心', '工人档案与薪资定义'],
          requiresAuth: true,
          permission: 'worker:manage'
        }
      },
      {
        path: 'production-plan-gantt',
        name: 'ProductionPlanGantt',
        component: () => import('../views/ProductionPlanGantt.vue'),
        meta: {
          title: '生产计划甘特图',
          breadcrumb: ['首页', '工程与生产管理', '生产计划甘特图'],
          requiresAuth: true
        }
      },
      {
        path: 'qc-report-management',
        name: 'QcReportManagement',
        component: () => import('../views/QcReportManagement.vue'),
        meta: {
          title: '质检报告管理',
          breadcrumb: ['首页', '订单与计划', '质检报告管理'],
          requiresAuth: true,
          permission: ['qc:inspect', 'qc:manage', 'order:view', 'prototype:view']
        }
      },
      {
        path: 'prototype-work-orders',
        name: 'PrototypeWorkOrderList',
        component: () => import('../views/PrototypeWorkOrderList.vue'),
        meta: {
          title: '打样工单管理',
          breadcrumb: ['首页', '产品与设计管理', '打样工单管理'],
          requiresAuth: true
        }
      },
      {
        path: 'prototype-work-orders/:id',
        name: 'PrototypeWorkspace',
        component: () => import('../views/PrototypeWorkspace.vue'),
        meta: {
          title: '打样工作台',
          breadcrumb: ['首页', '产品与设计管理', '打样工单管理', '打样工作台'],
          requiresAuth: true
        }
      },
      {
        path: 'sample-library',
        name: 'SampleLibrary',
        component: () => import('../views/SampleLibrary.vue'),
        meta: {
          title: '样衣库与样衣流转',
          breadcrumb: ['首页', '产品与设计管理', '样衣库'],
          requiresAuth: true
        }
      },
      {
        path: 'order-procurement-center',
        name: 'OrderProcurementCenter',
        component: () => import('../views/OrderProcurementCenter.vue'),
        meta: {
          title: '面料采购',
          breadcrumb: ['首页', '供应链与仓储', '面料采购'],
          requiresAuth: true
        }
      },
      // ===== v2.0 Phase 1 新增路由 =====
      {
        path: 'unit-conversion',
        name: 'UnitConversion',
        component: () => import('../views/UnitConversion.vue'),
        meta: {
          title: '单位换算规则',
          breadcrumb: ['首页', '基础配置中心', '单位换算规则'],
          requiresAuth: true
        }
      },
      // ===== v2.0 Phase 2 新增路由 =====
      {
        path: 'org-management',
        name: 'OrgManagement',
        component: () => import('../views/OrgManagement.vue'),
        meta: {
          title: '组织架构管理',
          breadcrumb: ['首页', '基础配置中心', '公司组织管理', '组织架构管理'],
          requiresAuth: true,
          permission: 'sys:org'
        }
      },
      {
        path: 'employee-management',
        name: 'EmployeeManagement',
        component: () => import('../views/EmployeeManagement.vue'),
        meta: {
          title: '员工档案',
          breadcrumb: ['首页', '基础配置中心', '公司组织管理', '员工档案'],
          requiresAuth: true,
          permission: 'sys:employee'
        }
      },
      {
        path: 'role-management',
        name: 'RoleManagement',
        component: () => import('../views/RoleManagement.vue'),
        meta: {
          title: '角色权限管理',
          breadcrumb: ['首页', '基础配置中心', '公司组织管理', '角色权限管理'],
          requiresAuth: true,
          permission: 'sys:role'
        }
      },
      {
        path: 'menu-management',
        name: 'MenuManagement',
        component: () => import('../views/MenuManagement.vue'),
        meta: {
          title: '菜单管理',
          breadcrumb: ['首页', '基础配置中心', '公司组织管理', '菜单管理'],
          requiresAuth: true,
          permission: 'system:menu:view'
        }
      },
      {
        path: 'qc-project-dictionary',
        name: 'QcProjectDictionary',
        component: () => import('../views/QcProjectDictionary.vue'),
        meta: {
          title: '质检项目库',
          breadcrumb: ['首页', '基础配置中心', '数据字典', '质检项目库'],
          requiresAuth: true,
          permission: ['qc:manage', 'sys:dict']
        }
      },
      {
        path: 'qc-template-management',
        name: 'QcTemplateManagement',
        component: () => import('../views/QcTemplateManagement.vue'),
        meta: {
          title: '质检模板',
          breadcrumb: ['首页', '基础配置中心', '数据字典', '质检模板'],
          requiresAuth: true,
          permission: ['qc:manage', 'sys:dict']
        }
      },
      // ===== 供应链与仓储 - 集采链路 =====
      {
        path: 'material-requirement',
        redirect: to => ({
          path: '/material-requirement-pool',
          query: to.query
        })
      },
      {
        path: 'material-requirement-pool',
        name: 'MaterialRequirementPool',
        component: () => import('../views/MaterialRequirementPool.vue'),
        meta: {
          title: '物料需求池',
          breadcrumb: ['首页', '供应链与仓储', '物料需求池'],
          requiresAuth: true
        }
      },
      {
        path: 'accessory-supply-workbench',
        name: 'AccessorySupplyWorkbench',
        component: () => import('../views/AccessorySupplyWorkbench.vue'),
        meta: {
          title: '辅料采购工作台',
          breadcrumb: ['首页', '供应链与仓储', '辅料采购工作台'],
          requiresAuth: true,
          permission: ['po:view', 'po:create', 'inventory:view', 'inventory:operate', 'warehouse:manage']
        }
      },
      {
        path: 'accessory-supply-workbench/purchase/new',
        name: 'AccessoryPurchaseOrderCreate',
        component: () => import('../views/AccessoryPurchaseOrderEdit.vue'),
        meta: {
          title: '创建辅料采购单',
          breadcrumb: ['首页', '供应链与仓储', '辅料采购工作台', '创建采购单'],
          requiresAuth: true,
          permission: 'po:create'
        }
      },
      {
        path: 'purchase-order-management',
        redirect: to => ({
          path: '/purchase-order-list',
          query: to.query
        })
      },
      {
        path: 'purchase-order-list',
        name: 'PurchaseOrderList',
        component: () => import('../views/PurchaseOrderList.vue'),
        meta: {
          title: '采购订单管理',
          breadcrumb: ['首页', '供应链与仓储', '采购订单管理'],
          requiresAuth: true,
          permission: ['po:view', 'po:create']
        }
      },
      {
        path: 'receiving-list',
        name: 'ReceivingList',
        component: () => import('../views/ReceivingList.vue'),
        meta: {
          title: '采购收货单',
          breadcrumb: ['首页', '供应链与仓储', '采购收货单'],
          requiresAuth: true
        }
      },
      {
        path: 'purchase-return-list',
        name: 'PurchaseReturnList',
        component: () => import('../views/PurchaseReturnList.vue'),
        meta: {
          title: '采购退供单',
          breadcrumb: ['首页', '供应链与仓储', '采购退供单'],
          requiresAuth: true,
          permission: ['inventory:operate', 'warehouse:manage']
        }
      },
      // ===== Phase 9: 供应商财务 + 库存操作 =====
      {
        path: 'supplier-finance-center',
        name: 'SupplierFinanceCenter',
        component: () => import('../views/SupplierFinanceCenter.vue'),
        meta: {
          title: '供应商应付中心',
          breadcrumb: ['首页', '供应链与仓储', '供应商应付中心'],
          requiresAuth: true,
          permission: 'finance:view'
        }
      },
      {
        path: 'inventory-operation-center',
        name: 'InventoryOperationCenter',
        component: () => import('../views/InventoryOperationCenter.vue'),
        meta: {
          title: '库存操作中心',
          breadcrumb: ['首页', '供应链与仓储', '库存操作中心'],
          requiresAuth: true
        }
      },
      {
        path: 'inventory-transaction-list',
        name: 'InventoryTransactionList',
        component: () => import('../views/InventoryTransactionList.vue'),
        meta: {
          title: '物料出入库明细',
          breadcrumb: ['首页', '供应链与仓储', '物料出入库明细'],
          requiresAuth: true
        }
      },
      // ===== v2.0 Phase 3 新增路由 =====
      {
        path: 'work-instructions',
        name: 'WorkInstructionList',
        component: () => import('../views/WorkInstructionList.vue'),
        meta: {
          title: '作业指导书',
          breadcrumb: ['首页', '生产执行', '作业指导书'],
          requiresAuth: true
        }
      },
      {
        path: 'cutting-execution',
        name: 'CuttingExecution',
        component: () => import('../views/CuttingExecution.vue'),
        meta: { title: '裁床执行工作台', breadcrumb: ['首页', '生产执行', '裁床执行工作台'], requiresAuth: true }
      },
      {
        path: 'shop-floor-execution',
        name: 'ShopFloorExecution',
        component: () => import('../views/ShopFloorExecution.vue'),
        meta: { title: '车间扫描计件报工终端', breadcrumb: ['首页', '生产执行', '车间报工终端'], requiresAuth: true }
      },
      {
        path: 'supervisor-rapid-report',
        name: 'SupervisorRapidReport',
        component: () => import('../views/SupervisorRapidReport.vue'),
        meta: { title: '主任极速核验台', breadcrumb: ['首页', '生产执行', '主任极速核验台'], requiresAuth: true }
      },
      {
        path: 'quality-control-station',
        name: 'QualityControlStation',
        component: () => import('../views/QualityControlStation.vue'),
        meta: { title: '质检工作站', breadcrumb: ['首页', '生产执行', '质检工作站'], requiresAuth: true, permission: ['qc:inspect', 'qc:manage'] }
      },
      {
        path: 'fqc-station',
        name: 'FinishedGoodsQC',
        component: () => import('../views/FinishedGoodsQC.vue'),
        meta: { title: '成品尾检工作台', breadcrumb: ['首页', '生产执行', '成品尾检工作台'], requiresAuth: true }
      },
      {
        path: 'task-board',
        name: 'TaskBoard',
        component: () => import('../views/TaskBoard.vue'),
        meta: { title: '工序任务管理', breadcrumb: ['首页', '生产执行', '工序任务管理'], requiresAuth: true }
      },
      {
        path: 'task-board/:id',
        name: 'TaskBoardDetail',
        component: () => import('../views/TaskDetail.vue'),
        meta: { title: '任务详情', breadcrumb: ['首页', '生产执行', '工序任务管理', '任务详情'], requiresAuth: true }
      },
      {
        path: 'outsource-order-center',
        name: 'OutsourceOrderCenter',
        component: () => import('../views/OutsourceOrderCenter.vue'),
        meta: { title: '外发订单管理', breadcrumb: ['首页', '生产执行', '外发订单管理'], requiresAuth: true, permission: 'outsource:manage' }
      },
      {
        path: 'outsource-supplier/:id',
        name: 'OutsourceSupplierProfile',
        component: () => import('../views/OutsourceSupplierProfile.vue'),
        meta: { title: '外协厂画像', breadcrumb: ['首页', '生产执行', '外发订单管理', '外协厂画像'], requiresAuth: true }
      },

      {
        path: 'fabric-inbound',
        name: 'FabricInbound',
        component: () => import('../views/FabricInbound.vue'),
        meta: { title: '面料入库台', breadcrumb: ['首页', '供应链与仓储', '面料入库台'], requiresAuth: true }
      },
      {
        path: 'fabric-inventory',
        name: 'FabricInventory',
        component: () => import('../views/FabricInventory.vue'),
        meta: { title: '面料库存大盘', breadcrumb: ['首页', '供应链与仓储', '面料库存大盘'], requiresAuth: true }
      },

      {
        path: 'warehouse-issue',
        name: 'WarehouseIssueStation',
        component: () => import('../views/WarehouseIssueStation.vue'),
        meta: { title: '仓库发料工作台', breadcrumb: ['首页', '供应链与仓储', '仓库发料工作台'], requiresAuth: true }
      },
      {
        path: 'coming-soon-stock-log',
        name: 'ComingSoonStockLog',
        component: () => import('../views/ComingSoon.vue'),
        meta: { title: '物料出入库明细（即将上线）', breadcrumb: ['首页', '供应链与仓储', '物料出入库明细'], requiresAuth: true }
      },
      {
        path: 'finished-goods-inbound',
        name: 'FinishedGoodsInbound',
        component: () => import('../views/FinishedGoodsInbound.vue'),
        meta: { title: '成品入库交接台', breadcrumb: ['首页', '供应链与仓储', '成品入库交接台'], requiresAuth: true }
      },
      {
        path: 'finished-goods-hub',
        name: 'FinishedGoodsHub',
        component: () => import('../views/FinishedGoodsHub.vue'),
        meta: { title: '成品库存中心', breadcrumb: ['首页', '供应链与仓储', '成品库存中心'], requiresAuth: true }
      },
      {
        path: 'coming-soon-supplier-ap',
        name: 'ComingSoonSupplierAP',
        component: () => import('../views/ComingSoon.vue'),
        meta: { title: '供应商账款（即将上线）', breadcrumb: ['首页', '业财与结算', '供应商账款'], requiresAuth: true }
      },
      {
        path: 'outsource-finance-center',
        name: 'OutsourceFinanceCenter',
        component: () => import('../views/OutsourceFinanceCenter.vue'),
        meta: { title: '外协厂账款', breadcrumb: ['首页', '业财与结算', '外协厂账款'], requiresAuth: true }
      },
      {
        path: 'customer-receivable-center',
        name: 'CustomerReceivableCenter',
        component: () => import('../views/CustomerReceivableCenter.vue'),
        meta: { title: '客户应收中心', breadcrumb: ['首页', '业财与结算', '客户应收中心'], requiresAuth: true }
      },
      {
        path: 'payroll-management',
        name: 'PayrollManagement',
        component: () => import('../views/PayrollManagement.vue'),
        meta: { title: '员工工资核算', breadcrumb: ['首页', '业财与结算', '员工工资核算'], requiresAuth: true }
      },
      {
        path: 'fee-management',
        name: 'FeeManagement',
        component: () => import('../views/FeeManagement.vue'),
        meta: { title: '费用管理', breadcrumb: ['首页', '业财与结算', '费用管理'], requiresAuth: true }
      },
      {
        path: 'cost-decision-center',
        name: 'CostDecisionCenter',
        component: () => import('../views/CostDecisionCenter.vue'),
        meta: { title: '商品成本决算', breadcrumb: ['首页', '业财与结算', '商品成本决算'], requiresAuth: true }
      },
      {
        path: 'coming-soon-costing',
        redirect: '/cost-decision-center'
      },
      {
        path: 'label-template-center',
        name: 'LabelTemplateCenter',
        component: () => import('../views/LabelTemplateCenter.vue'),
        meta: { title: '标签模板与打印中心', breadcrumb: ['首页', '基础配置中心', '数据字典', '标签模板与打印中心'], requiresAuth: true, permission: ['label-template:view', 'label-print:view', 'label-print:execute', 'sys:dict'] }
      },
      {
        path: 'coming-soon-print-tpl',
        redirect: '/label-template-center'
      },
      {
        path: 'sys-dict-management',
        name: 'SysDictManagement',
        component: () => import('../views/SysDictManagement.vue'),
        meta: { title: '系统字典管理', breadcrumb: ['首页', '基础配置中心', '数据字典', '系统字典管理'], requiresAuth: true, permission: 'sys:dict' }
      }
    ]
  },
  // ======= 工人移动端路由 (独立于 PC 端) =======
  {
    path: '/m/login',
    name: 'WorkerLogin',
    component: () => import('../views/mobile/WorkerLogin.vue'),
    meta: { title: '工人登录', mobile: true, public: true }
  },
  {
    path: '/m',
    component: () => import('../layouts/MobileLayout.vue'),
    meta: { mobile: true },
    children: [
      {
        path: '',
        name: 'WorkerHome',
        component: () => import('../views/mobile/WorkerHome.vue'),
        meta: { title: '工人首页', mobile: true }
      },
      {
        path: 'tickets',
        name: 'TicketList',
        component: () => import('../views/mobile/TicketList.vue'),
        meta: { title: '任务报工', mobile: true }
      },
      {
        path: 'scan',
        name: 'MobileWorkerScan',
        component: () => import('../views/ShopFloorExecution.vue'),
        meta: { title: '个人扫码报工', mobile: true }
      },
      {
        path: 'team-scan',
        name: 'MobileTeamScan',
        component: () => import('../views/ShopFloorExecution.vue'),
        meta: { title: '班组公共报工台', mobile: true }
      },
      {
        path: 'shop-floor',
        name: 'MobileShopFloor',
        component: () => import('../views/ShopFloorExecution.vue'),
        meta: { title: '车间公共报工台', mobile: true, legacyMobileShopFloor: true }
      },
      {
        path: 'tickets/:ticketId',
        name: 'TicketDetail',
        component: () => import('../views/mobile/TicketDetail.vue'),
        meta: { title: '工票详情', mobile: true }
      },
      {
        path: 'calendar',
        name: 'ProductionCalendar',
        component: () => import('../views/mobile/ProductionCalendar.vue'),
        meta: { title: '生产日历', mobile: true }
      },
      {
        path: 'calendar/:date',
        name: 'DailyDetail',
        component: () => import('../views/mobile/DailyDetail.vue'),
        meta: { title: '产量明细', mobile: true }
      },
      {
        path: 'profile',
        name: 'WorkerProfile',
        component: () => import('../views/mobile/WorkerProfile.vue'),
        meta: { title: '我的', mobile: true }
      },
      {
        path: 'company-dashboard',
        name: 'MobileCompanyDashboard',
        component: () => import('../views/mobile/CompanyDashboard.vue'),
        meta: { title: '公司移动看板', mobile: true }
      },
      {
        path: 'receiving',
        name: 'MobileReceiving',
        component: () => import('../views/mobile/MobileReceiving.vue'),
        meta: { title: '仓库收货', mobile: true }
      },
      {
        path: 'cutting',
        name: 'CuttingPDA',
        component: () => import('../views/mobile/CuttingPDA.vue'),
        meta: { title: '裁床报工', mobile: true }
      },
      {
        path: 'cutting/:taskId',
        name: 'CuttingPDAExec',
        component: () => import('../views/mobile/CuttingPDAExec.vue'),
        meta: { title: '裁剪执行', mobile: true }
      },
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(appBase),
  routes
})
import { workerAuthState, restoreWorkerSession, isSupervisorWorker, isBossMobileWorker } from '../config/workerAuth'

// Phase 11: 路由守卫 — 登录验证 + 权限校验
router.beforeEach(async (to, _from, next) => {
  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - 易智造系统`
  }

  // ===== 移动端路由守卫（独立于PC端） =====
  const isMobile = to.matched.some(r => r.meta.mobile)
  if (isMobile) {
    // 公开页面（登录页）
    if (to.meta.public) return next()

    // 尝试恢复工人会话
    if (!workerAuthState.isLoggedIn) {
      await restoreWorkerSession()
    }

    // 未登录拦截
    if (!workerAuthState.isLoggedIn) {
      return next('/m/login')
    }

    const hasScanContext = Boolean(to.query.taskId && (to.query.opId || to.query.l2OperationId))
    if (to.path === '/m/shop-floor') {
      if (hasScanContext) {
        return next({ path: '/m/scan', query: to.query, replace: true })
      }
      if (isSupervisorWorker(workerAuthState.currentWorker as any)) {
        return next({ path: '/m/team-scan', query: to.query, replace: true })
      }
      return next('/m/tickets')
    }

    if (to.path === '/m/team-scan' && !isSupervisorWorker(workerAuthState.currentWorker as any)) {
      return next('/m/tickets')
    }

    if (to.path === '/m/company-dashboard' && !isBossMobileWorker(workerAuthState.currentWorker as any)) {
      return next('/m/')
    }

    if (to.path === '/m/scan' && !hasScanContext) {
      return next('/m/tickets')
    }

    return next()
  }

  // ===== PC端路由守卫 =====
  const isLoggedIn = authState.isLoggedIn
  const requiresAuth = to.matched.some(r => r.meta.requiresAuth)

  // 根路径重定向
  if (to.path === '/') {
    return next(isLoggedIn ? '/dashboard' : '/login')
  }

  // 未登录拦截
  if (requiresAuth && !isLoggedIn) {
    return next('/login')
  }

  // 已登录访问登录页
  if (isLoggedIn && to.path === '/login') {
    return next('/dashboard')
  }

  // Phase 11: 路由级权限校验
  const requiredPerm = resolveRoutePermission(to)
  if (requiredPerm && isLoggedIn && !hasPermission(requiredPerm)) {
    const roleName = getPrimaryRoleName()
    const pageTitle = String(to.meta.title || '该页面')
    const permissionText = Array.isArray(requiredPerm) ? requiredPerm.join(',') : requiredPerm
    toast.warning(`您当前的【${roleName}】角色暂无查看「${pageTitle}」的权限`)
    return next({
      path: '/403',
      query: {
        redirect: to.fullPath,
        title: pageTitle,
        permission: permissionText
      }
    })
  }

  if (isLoggedIn && to.path !== '/403' && !isConfiguredMenuPathAllowed(to.path)) {
    const pageTitle = String(to.meta.title || '该页面')
    toast.warning(`菜单「${pageTitle}」已停用或对当前角色不可见`)
    return next({
      path: '/403',
      query: {
        redirect: to.fullPath,
        title: pageTitle,
        permission: 'menu:disabled'
      }
    })
  }

  next()
})

export default router
